import { useState, useEffect, useRef } from 'react';
import {
  IconUp,
  IconDown,
  IconTrash,
  ButtonSecondary,
  ButtonSize,
  IconExpand,
  IconBlock,
  IconUpload,
  IconSelected,
  IconDownload,
} from '@ui/src';
import FilterPresetContent from './filter-preset-content';
import { FiltersPresetTypes } from './types';

import cn from './profile.module.css';

const getNoun = (number: number, one: string, two: string, five: string) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }

  n %= 10;
  if (n === 1) {
    return one;
  }

  if (n >= 2 && n <= 4) {
    return two;
  }

  return five;
};

const FiltersPreset = ({
  userId,
  filtersPresetKey,
  title,
  columnsLabels,
  filtersSchema,
}: FiltersPresetTypes) => {
  const [filtersPresets, setFiltersPresets] = useState<
    {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[]
  >([]);
  const [filtersPresetsVisible, setFiltersPresetsVisible] = useState(false);
  const [uploadErrorTextVisible, setUploadErrorTextVisible] = useState(false);
  const [uploadFilterSuccess, setUploadFilterSuccess] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [selectedPresets, setSelectedPresets] = useState<
    {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[]
  >([]);

  const importInputRef = useRef<HTMLInputElement | null>(null);
  const reuploadFiltersPresetRef = useRef<HTMLInputElement | null>(null);

  const onDeleteAll = () => {
    const filtersPresetJSON = localStorage.getItem(filtersPresetKey);

    if (filtersPresetJSON) {
      const parsed = JSON.parse(filtersPresetJSON);

      if (parsed) {
        const nextFiltersPresets = parsed?.filter((item: any) =>
          filtersPresets?.every((preset) => item.id !== preset.id),
        );

        setFiltersPresets([]);

        if (nextFiltersPresets?.length) {
          localStorage.setItem(
            filtersPresetKey,
            JSON.stringify(
              nextFiltersPresets?.map((item: any, index: any) => ({
                ...item,
                id: `${filtersPresetKey}-${index + 1}`,
              })),
            ),
          );
        } else {
          localStorage.removeItem(filtersPresetKey);
        }
      }
    }
  };

  const onDownloadPresets = () => {
    const fileContent: {
      key: string;
      values?: {
        id: string;
        name: string;
        value: Record<string, any>;
        default?: boolean;
        employeeId: string;
      }[];
    } = { key: filtersPresetKey };

    if (selectedPresets?.length === filtersPresets?.length) {
      const savedFiltersJSON = localStorage.getItem(filtersPresetKey);

      if (savedFiltersJSON) {
        const parsed = JSON.parse(savedFiltersJSON);

        if (parsed) {
          fileContent.values = parsed?.filter(
            (filterItem: any) => filterItem.employeeId === userId,
          );
        }
      }
    } else {
      fileContent.values = selectedPresets;
    }

    const blob = new Blob([JSON.stringify(fileContent)], {
      type: 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    const savedFiltersName = filtersPresetKey
      ?.split('-')
      ?.map((key) => `${key.charAt(0).toUpperCase()}${key.slice(1)}`)
      ?.join(' ');

    a.href = url;
    a.download = `${savedFiltersName} Preset`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setSelectedPresets([]);
  };

  const onUploadFiltersPreset = (files: FileList) => {
    setUploadErrorTextVisible(false);

    const file = files[0];

    const reader = new FileReader();

    reader.onload = (evt) => {
      const fileContent = evt.target?.result;

      if (fileContent && typeof fileContent === 'string') {
        try {
          const parsed = JSON.parse(fileContent);

          if (parsed) {
            if (parsed.key === filtersPresetKey) {
              let nextSavedFilters;

              const currentSavedFiltersJSON =
                localStorage.getItem(filtersPresetKey);

              if (currentSavedFiltersJSON) {
                const parsedCurrentSavedFilters = JSON.parse(
                  currentSavedFiltersJSON,
                );

                nextSavedFilters = parsedCurrentSavedFilters;

                parsed.values?.forEach((filterValue: any) => {
                  nextSavedFilters.push({
                    ...filterValue,
                    employeeId: userId,
                    default: false,
                  });
                });
              } else {
                nextSavedFilters = parsed.values?.map(
                  (filterItem: any, index: number) => ({
                    ...filterItem,
                    id: `${filtersPresetKey}-${index + 1}`,
                    employeeId: userId,
                    default: false,
                  }),
                );
              }

              nextSavedFilters = nextSavedFilters?.map(
                (filterItem: any, index: number) => ({
                  ...filterItem,
                  id: `${filtersPresetKey}-${index + 1}`,
                }),
              );

              setFiltersPresets(
                nextSavedFilters?.filter(
                  (filterItem: any) => filterItem.employeeId === userId,
                ),
              );

              setUploadFilterSuccess(true);
              setUploadedFileName(file.name);

              localStorage.setItem(
                filtersPresetKey,
                JSON.stringify(nextSavedFilters),
              );

              if (importInputRef?.current) {
                importInputRef.current.value = '';
              }

              if (reuploadFiltersPresetRef?.current) {
                reuploadFiltersPresetRef.current.value = '';
              }
            } else {
              setUploadErrorTextVisible(true);
              setUploadFilterSuccess(false);

              if (importInputRef?.current) {
                importInputRef.current.value = '';
              }

              if (reuploadFiltersPresetRef?.current) {
                reuploadFiltersPresetRef.current.value = '';
              }
            }
          }
        } catch {
          setUploadErrorTextVisible(true);
          setUploadFilterSuccess(false);

          if (importInputRef?.current) {
            importInputRef.current.value = '';
          }

          if (reuploadFiltersPresetRef?.current) {
            reuploadFiltersPresetRef.current.value = '';
          }
        }
      }
    };

    reader.readAsText(file);
  };

  const onDeleteSelectedPresets = () => {
    const savedFiltersJSON = localStorage.getItem(filtersPresetKey);

    if (savedFiltersJSON) {
      const parsed: {
        id: string;
        name: string;
        value: Record<string, any>;
        default?: boolean;
        employeeId: string;
      }[] = JSON.parse(savedFiltersJSON);

      const nextSavedFilters = parsed
        ?.filter((filterItem) =>
          selectedPresets?.every((item) => filterItem.id !== item.id),
        )
        ?.map((filterItem, index) => ({
          ...filterItem,
          id: `${filtersPresetKey}-${index + 1}`,
        }));

      const currentUserSavedFilters = nextSavedFilters?.filter(
        (filterItem) => filterItem.employeeId === userId,
      );

      setFiltersPresets(
        currentUserSavedFilters,
      );

      if (nextSavedFilters?.length) {
        localStorage.setItem(
          filtersPresetKey,
          JSON.stringify(nextSavedFilters),
        );
      } else {
        localStorage.removeItem(filtersPresetKey);
      }

      setSelectedPresets([]);
    }
  };

  const importPresetContent = (
    <div className={cn.importInputWrapper}>
      <ButtonSecondary
        text="Import Filter Preset"
        icon={<IconExpand width={16} height={16} />}
        className={`button-white ${cn.importButton}`}
        size={ButtonSize.SMALL}
        onClick={() => importInputRef?.current?.click()}
      />
      <input
        type="file"
        className={cn.importInput}
        ref={importInputRef}
        accept=".txt"
        onChange={(e) => onUploadFiltersPreset(e.target.files as FileList)}
      />
    </div>
  );

  useEffect(() => {
    if (uploadFilterSuccess) {
      setTimeout(() => {
        setUploadFilterSuccess(false);
      }, 10000);
    }
  }, [uploadFilterSuccess]);

  useEffect(() => {
    if (userId && filtersPresetKey) {
      const filtersPresetJSON = localStorage.getItem(filtersPresetKey);

      if (filtersPresetJSON) {
        const parsed = JSON.parse(filtersPresetJSON);

        if (parsed) {
          const currentUserPresets = parsed?.filter(
            (item: any) => item.employeeId === userId,
          );

          setFiltersPresets(currentUserPresets);
        }
      }
    }
  }, [userId, filtersPresetKey]);

  return (
    <>
      <div className={cn.filtersPresetsWrapper}>
        <div className={cn.filtersPresetsLeft}>
          <div className={cn.filtersPresetTitle}>{title}</div>
        </div>
        <div className={cn.filtersPresetRightContentWrapper}>
          <div className={cn.filtersPresetRightContent}>
            <div className={cn.filtersPresetsButtonsWrapper}>
              {filtersPresets?.length > 0 && (
                <button
                  type="button"
                  className={cn.showPresetsButton}
                  onClick={() =>
                    setFiltersPresetsVisible(!filtersPresetsVisible)
                  }
                >
                  {`${filtersPresets?.length} ${getNoun(
                    filtersPresets?.length,
                    'preset',
                    'presets',
                    'presets',
                  )}`}
                  {filtersPresetsVisible ? <IconUp /> : <IconDown />}
                </button>
              )}
              {!filtersPresets.length && importPresetContent}
            </div>
            {filtersPresets?.length > 0 && (
              <div className={cn.filtersPresetsRight}>
                {selectedPresets && selectedPresets?.length > 0 && (
                  <>
                    <button
                      type="button"
                      className={cn.downloadPresetsButton}
                      onClick={onDownloadPresets}
                    >
                      <IconDownload />
                      {`Export ${selectedPresets?.length} ${getNoun(
                        selectedPresets?.length,
                        'preset',
                        'presets',
                        'presets',
                      )}`}
                    </button>
                    <button
                      type="button"
                      className={cn.deletePresetsButton}
                      onClick={onDeleteSelectedPresets}
                    >
                      <IconTrash width={17} height={17} /> Delete
                    </button>
                  </>
                )}
                {importPresetContent}
                <button
                  type="button"
                  className={cn.deleteAllButton}
                  onClick={onDeleteAll}
                >
                  <IconTrash width={18} /> Delete all
                </button>
              </div>
            )}
          </div>
          {filtersPresetsVisible && (
            <div className={cn.filtersPresetList}>
              {filtersPresets?.map((item) => (
                <FilterPresetContent
                  key={item.id}
                  item={item}
                  selectedPresets={selectedPresets}
                  setSelectedPresets={setSelectedPresets}
                  columnsLabels={columnsLabels}
                  filtersSchema={filtersSchema}
                  filtersPresetKey={filtersPresetKey}
                  setFiltersPresets={setFiltersPresets}
                  userId={userId}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {uploadErrorTextVisible && (
        <div className={cn.uploadedError}>
          <div className={cn.uploadedErrorLeft}>
            <div className={cn.uploadedErrorIconWrapper}>
              <IconBlock width={20} />
            </div>
            <div className={cn.uploadErrorText}>
              The uploaded file is invalid. Please re-download the file with
              filters and try <br /> to upload them again.
            </div>
          </div>
          <div className={cn.uploadedErrorRight}>
            <div className={cn.uploadedErrorRightInputWrapper}>
              <ButtonSecondary
                size={ButtonSize.SMALL}
                text="Select file"
                className={`button-white ${cn.uploadFileButton}`}
                icon={<IconUpload />}
                onClick={() => reuploadFiltersPresetRef?.current?.click()}
              />
              <input
                type="file"
                className={cn.uploadInput}
                ref={reuploadFiltersPresetRef}
                onChange={(e) =>
                  onUploadFiltersPreset(e.target.files as FileList)
                }
                accept=".txt"
              />
            </div>
            <div className={cn.uploadErrorCloseButton}>
              <ButtonSecondary
                size={ButtonSize.SMALL}
                text="Close"
                className={`button-white ${cn.uploadFileButton}`}
                onClick={() => setUploadErrorTextVisible(false)}
              />
            </div>
          </div>
        </div>
      )}
      {uploadFilterSuccess && (
        <div className={cn.uploadedFileSuccess}>
          <div className={cn.uploadedFileSuccessLeft}>
            <div className={cn.uploadedFileSuccessIconWrapper}>
              <IconSelected width={20} />
            </div>
            <div
              className={cn.uploadedFileSuccessText}
            >{`Preset "${uploadedFileName}" added`}</div>
          </div>
          <div className={cn.uploadedFileSuccessCloseWrapper}>
            <ButtonSecondary
              size={ButtonSize.SMALL}
              text="Close"
              className={`button-white ${cn.closeButton}`}
              onClick={() => {
                setUploadFilterSuccess(false);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FiltersPreset;
